import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useFieldArray, useWatch } from 'react-hook-form';

import { GenericQuestion } from '@components/organisms';

const GenericForm = ({
    control, fieldName, formState, getValues, questions, setValue, surveyType,
}) => {
    const { fields } = useFieldArray({
        control,
        name: fieldName,
    });
    const watchedAnswers = useWatch({ control, name: fieldName });

    const questionNumOrderIndexMap = useMemo(() => questions.reduce((acc, q, index) => {
        if (q.num_order)
            acc[q.num_order] = index;

        return acc;
    }, {}), [questions]);

    const visibilityMap = useMemo(() => {
        const cache = {};

        const checkVisibility = (qIndex) => {
            if (cache[qIndex] !== undefined)
                return cache[qIndex];

            const question = questions[qIndex];
            const rule = question?.visibility_rule;

            if (!rule) {
                cache[qIndex] = true;
                return true;
            }

            const evaluateRule = (r) => {
                if (!r)
                    return true;

                if (r.type === 'condition' && r.operator === 'EQUALS') {
                    const parentIdx = questionNumOrderIndexMap[r.parent_num_order];
                    if (parentIdx === undefined)
                        return false;

                    if (!checkVisibility(parentIdx))
                        return false;

                    const parentAnswer = watchedAnswers?.[parentIdx]?.answer;
                    if (parentAnswer === null || parentAnswer === undefined)
                        return false;

                    if (Array.isArray(parentAnswer))
                        return r.values.some((val) => parentAnswer.includes(val));

                    return r.values.includes(parentAnswer);
                }

                if (r.type === 'group') {
                    if (r.operator === 'AND')
                        return r.conditions.every(evaluateRule);
                    if (r.operator === 'OR')
                        return r.conditions.some(evaluateRule);
                }

                return false;
            };

            const result = evaluateRule(rule);
            cache[qIndex] = result;
            return result;
        };

        return questions.map((_, idx) => checkVisibility(idx));
    }, [questions, questionNumOrderIndexMap, watchedAnswers]);

    useEffect(() => {
        if (!watchedAnswers)
            return;

        questions.forEach((question, idx) => {
            if (!visibilityMap[idx]) {
                const currentAnswer = watchedAnswers[idx]?.answer;
                const isArrayField = question.field === 'CLOSED_TEXT_ARRAY';
                const resetValue = isArrayField ? [] : null;

                const needsReset = isArrayField
                    ? !Array.isArray(currentAnswer) || currentAnswer.length > 0
                    : currentAnswer !== null && currentAnswer !== undefined;

                if (needsReset)
                    setValue(`${fieldName}.${idx}.answer`, resetValue, { shouldValidate: true });
            }
        });
    }, [visibilityMap, questions, setValue, watchedAnswers]);

    let fieldIndex = 0;

    return (
        <>
            {fields.map((field, idx) => {
                const question = questions[idx];

                if (!visibilityMap[idx])
                    return null;

                const isSubheading = question.field === 'SUBHEADING';

                if (!isSubheading)
                    fieldIndex += 1;

                return (
                    <GenericQuestion
                        control={control}
                        field={field}
                        fieldIndex={fieldIndex}
                        fieldName={fieldName}
                        formState={formState}
                        getValues={getValues}
                        idx={idx}
                        key={field.id}
                        question={question}
                        setValue={setValue}
                        surveyType={surveyType}
                    />
                );
            })}
        </>
    );
};

GenericForm.defaultProps = {
    fieldName: 'answers',
};

GenericForm.propTypes = {
    control: PropTypes.shape({
        register: PropTypes.func,
    }).isRequired,
    fieldName: PropTypes.string,
    formState: PropTypes.shape({
        errors: PropTypes.shape({
            answers: PropTypes.arrayOf(
                PropTypes.shape({
                    answer: PropTypes.shape({
                        message: PropTypes.string,
                    }),
                }),
            ),
        }),
    }).isRequired,
    getValues: PropTypes.func.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        visibility_rule: PropTypes.shape({
            type: PropTypes.string,
        }),
    })).isRequired,
    setValue: PropTypes.func.isRequired,
    surveyType: PropTypes.string.isRequired,
};

export default GenericForm;
